import { Clock, DollarSign, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SettingCard } from "@/components/settings/setting-card";
import { Badge } from "@/components/ui/badge";
import { useSalonStore } from "@/lib/store/salon";
import { TimezoneService } from "@/lib/timezone";

interface CurrencyTimezoneInfoCardProps {
  /** Loading state for initial data fetch */
  loading?: boolean;
}

export function CurrencyTimezoneInfoCard({ loading = false }: CurrencyTimezoneInfoCardProps) {
  const { t, i18n } = useTranslation("settings");
  const { currentSalon } = useSalonStore();

  // Get user-friendly timezone display
  const timezoneDisplay = currentSalon
    ? TimezoneService.getTimezoneDisplayName(currentSalon.timezone, i18n.language)
    : { friendlyName: "", technicalName: "" };

  if (loading || !currentSalon) {
    return (
      <SettingCard
        title={t("business.currencyTimezone.title")}
        description={t("business.currencyTimezone.description")}
      >
        <div className="space-y-4">
          <div className="h-4 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
        </div>
      </SettingCard>
    );
  }

  return (
    <SettingCard
      title={t("business.currencyTimezone.title")}
      description={t("business.currencyTimezone.description")}
      helpText={t("business.currencyTimezone.helpText")}
      helpDetails={
        <div>
          <div className="font-medium mb-2">{t("business.currencyTimezone.helpDetails.title")}</div>
          <ul className="space-y-1 text-sm">
            {(
              t("business.currencyTimezone.helpDetails.benefits", {
                returnObjects: true,
              }) as string[]
            ).map((benefit: string, index: number) => (
              <li key={index}>• {benefit}</li>
            ))}
          </ul>
        </div>
      }
      icon={<Info className="h-5 w-5 text-muted-foreground" />}
    >
      <div className="space-y-6">
        {/* Currency Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t("business.currencyTimezone.currency")}</span>
          </div>
          <Badge variant="secondary" className="text-sm">
            {currentSalon.currency}
          </Badge>
        </div>

        {/* Timezone Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t("business.currencyTimezone.timezone")}</span>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge variant="secondary" className="text-sm">
              {timezoneDisplay.friendlyName}
            </Badge>
            {timezoneDisplay.friendlyName !== timezoneDisplay.technicalName && (
              <span className="text-xs text-muted-foreground font-mono">
                {timezoneDisplay.technicalName}
              </span>
            )}
          </div>
        </div>
      </div>
    </SettingCard>
  );
}
